/**
 * What the portal reads, and how long it holds it.
 *
 * Nothing here decides what a patient may see. The Consent Decision Point does
 * that server-side on every request (§1.6, §14.2); these hooks only fetch and
 * cache. A hook that filtered would be UI hiding masquerading as access
 * control.
 *
 * Keyed by patient id even though a portal session has exactly one patient:
 * the key is what makes a stale cache impossible if a different account signs
 * in on the same tab.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import type {
  FhirCondition,
  FhirEncounter,
  FhirMedicationRequest,
  FhirObservation,
  FhirPatient,
} from '@glycemia/web-kit';

import {
  readAccessReport,
  readConsents,
  readOwnRecord,
  searchForPatient,
  type ConsentGrant,
} from './api.js';

const shared = { staleTime: 30_000, retry: 1 } as const;

export const useMe = (reference: string): UseQueryResult<FhirPatient> =>
  useQuery({
    queryKey: ['me', reference],
    queryFn: () => readOwnRecord<FhirPatient>(reference),
    ...shared,
  });

export const useMyConditions = (patientId: string): UseQueryResult<readonly FhirCondition[]> =>
  useQuery({
    queryKey: ['Condition', patientId],
    queryFn: () => searchForPatient<FhirCondition>('Condition', patientId),
    ...shared,
  });

export const useMyMedications = (
  patientId: string,
): UseQueryResult<readonly FhirMedicationRequest[]> =>
  useQuery({
    queryKey: ['MedicationRequest', patientId],
    queryFn: () => searchForPatient<FhirMedicationRequest>('MedicationRequest', patientId),
    ...shared,
  });

export const useMyObservations = (
  patientId: string,
  category?: string,
): UseQueryResult<readonly FhirObservation[]> =>
  useQuery({
    queryKey: ['Observation', patientId, category ?? 'all'],
    queryFn: () =>
      searchForPatient<FhirObservation>(
        'Observation',
        patientId,
        category === undefined ? '' : `&category=${encodeURIComponent(category)}`,
      ),
    ...shared,
  });

export const useMyVisits = (patientId: string): UseQueryResult<readonly FhirEncounter[]> =>
  useQuery({
    queryKey: ['Encounter', patientId],
    queryFn: () => searchForPatient<FhirEncounter>('Encounter', patientId),
    ...shared,
  });

export const useAccessReport = (patientId: string): UseQueryResult<readonly unknown[]> =>
  useQuery({
    queryKey: ['access-report', patientId],
    queryFn: () => readAccessReport(patientId),
    ...shared,
  });

export const useConsents = (patientId: string): UseQueryResult<readonly ConsentGrant[]> =>
  useQuery({
    queryKey: ['consents', patientId],
    queryFn: () => readConsents(patientId),
    ...shared,
  });
